import { AfterViewInit, Component, ElementRef, Injector, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

// Rxjs
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

// Services
import { AuthService } from '@auth/auth.service';
import { WebcomponentsService } from '@core/service/webcomponents.service';

// Configs
import { DEFAULT_PROCESS_ID_CONSTANT, DISPERSION_METHOD_COMPONENTS_CONSTANT } from './configs';
import { ROUTES_CONSTANTS } from '@shared/configs';

// Models
import { ProductType } from '@core/model/enums';

// Strategy
import { dispersionMethodRegistryStrategy } from './strategy';

import { CHECK_PROCESS_STATE_ENUMERATION, checkProcessRedirect } from '@core/configs/check-process';

export interface DispersionMethodBuildOptionsType {
  viewName?: string;
  processId?: string;
  productId?: string;
  email?: string;
  finalProcessResult?: Function;
  alternativeNavigationFn: Function;
}
@Component({
  selector: 'app-dispersion-method',
  templateUrl: './dispersion-method.component.html',
  styleUrls: ['./dispersion-method.component.scss'],
})
export class DispersionMethodComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dispersionMethodRef', { static: false }) public dispersionMethodComponent: ElementRef;

  public isDispersionMethodLoading: boolean;
  private readonly unSubscribe$: Subject<void>;
  private readonly webComponentService: WebcomponentsService;
  private readonly authService: AuthService;
  private readonly router: Router;
  private readonly ngZone: NgZone;
  private readonly injectorInstance: Injector;

  constructor(private readonly injector: Injector) {
    this.webComponentService = this.injector.get(WebcomponentsService);
    this.authService = this.injector.get(AuthService);
    this.router = this.injector.get(Router);
    this.ngZone = this.injector.get(NgZone);
    this.injectorInstance = injector;

    this.isDispersionMethodLoading = true;
    this.unSubscribe$ = new Subject<void>();
  }

  public ngAfterViewInit(): void {
    this.webComponentService.isLoaded$.pipe(filter(Boolean), takeUntil(this.unSubscribe$)).subscribe({
      next: () => {
        this.renderDispersionMethodWebComponente();
      },
      error: (err) => {
        console.error('Error', err);
        this.router.navigate([ROUTES_CONSTANTS.HOME]);
      },
    });
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  private renderDispersionMethodWebComponente(): void {
    const dispersionMethod = this.dispersionMethodComponent.nativeElement;

    dispersionMethod.ngElementStrategy?.componentRef?.instance?.build({
      viewName: DISPERSION_METHOD_COMPONENTS_CONSTANT.VERIFICATION_SELECT_ACCOUNT,
      processId: DEFAULT_PROCESS_ID_CONSTANT,
      email: this.authService.user?.email,
      finalProcessResult: this.finalProcessResult.bind(this),
      alternativeNavigationFn: this.alternativeNavigationFn.bind(this),
    } as DispersionMethodBuildOptionsType);

    setTimeout(() => {
      this.isDispersionMethodLoading = false;
    }, 0);
  }

  public finalProcessResult({
    state,
    args,
    productType,
  }: {
    state: string;
    args?: unknown;
    productType: string;
  }): void {
    const origin = productType === ProductType.RP ? ProductType.RP : ProductType.RFYA;
    const strategy = dispersionMethodRegistryStrategy[origin]?.({
      injector: this.injectorInstance,
    });

    if (strategy?.finalProcessFunc) {
      strategy.finalProcessFunc({ state, args });
    }
  }

  public alternativeNavigationFn({ state }: { state: CHECK_PROCESS_STATE_ENUMERATION }): void {
    this.ngZone.run(() => {
      const route = checkProcessRedirect(state, [CHECK_PROCESS_STATE_ENUMERATION.PENDING_DISPERSION_METHOD]);

      if (route) {
        this.router.navigate([route]);
      }
    });
  }
}
